import { useState } from 'react';
import { motion } from 'framer-motion';
import { useWallet } from '@txnlab/use-wallet';
import Button from '@/components/Button';
import useSKClubAuth from '@/hooks/useSKClubAuth';
import { useBalanceStore } from '@/hooks/useBalanceStore';

const cardVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, delay: i * 0.1 },
  }),
};

const DashboardCard = ({ children, index = 0, glowColor = 'from-gray-600/30 to-gray-600/30' }) => (
  <motion.div
    custom={index}
    initial='hidden'
    animate='visible'
    variants={cardVariants}
    className='relative group'
  >
    <div className={`absolute -inset-[1px] bg-gradient-to-r ${glowColor} rounded-xl blur-[1px] group-hover:blur-[2px] transition-all duration-300`} />
    <div className='relative bg-gray-800 rounded-xl p-6 border border-gray-700/50 h-full'>
      {children}
    </div>
  </motion.div>
);

export default function SKClubDashboard() {
  const { skPerks, coinBalance, getIdToken, firebaseUser } = useSKClubAuth();
  const { activeAddress, providers } = useWallet();
  const { walletBalance } = useBalanceStore();
  const [verifying, setVerifying] = useState(false);

  const handleVerify = async () => {
    if (!activeAddress || !firebaseUser) return;

    setVerifying(true);
    try {
      const idToken = await getIdToken();
      const response = await fetch('/api/sk-club/verify-holder', {
        method: 'POST',
        headers: {
          authorization: process.env.NEXT_PUBLIC_API_KEY,
          'x-firebase-token': idToken,
        },
        body: JSON.stringify({ walletAddress: activeAddress }),
      });

      const data = await response.json();
      if (data.success) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Verification failed:', error);
    } finally {
      setVerifying(false);
    }
  };

  const connectWallet = () => {
    const peraProvider = providers?.find((p) => p.metadata.id === 'pera');
    if (peraProvider) {
      peraProvider.connect();
    }
  };

  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <DashboardCard index={0} glowColor='from-primary-500/20 via-transparent to-primary-500/20'>
          <div className='flex items-center gap-2 mb-4'>
            <div className='w-2 h-2 rounded-full bg-primary-500' />
            <h3 className='text-sm font-bold text-gray-400 uppercase tracking-wider'>Wallet</h3>
          </div>
          {activeAddress ? (
            <div>
              <p className='text-white font-mono text-sm bg-gray-700/50 rounded-lg px-3 py-2 inline-block'>
                {activeAddress.slice(0, 6)}...{activeAddress.slice(-4)}
              </p>
              <div className='mt-4 pt-4 border-t border-gray-700/50'>
                <p className='text-xs text-gray-500 uppercase tracking-wider font-medium'>TREATS Balance</p>
                <p className='text-2xl font-bold text-primary-400 mt-1'>{walletBalance.toLocaleString()}</p>
              </div>
            </div>
          ) : (
            <div>
              <p className='text-gray-400 text-sm mb-4'>Connect your wallet to verify SK holder status</p>
              <Button onClick={connectWallet}>Connect Wallet</Button>
            </div>
          )}
        </DashboardCard>

        <DashboardCard index={1} glowColor='from-green-500/20 via-transparent to-green-500/20'>
          <div className='flex items-center gap-2 mb-4'>
            <div className='w-2 h-2 rounded-full bg-green-500' />
            <h3 className='text-sm font-bold text-gray-400 uppercase tracking-wider'>SK Holder Status</h3>
          </div>
          {skPerks ? (
            <div>
              <div
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                  skPerks.isSkHolder ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-gray-700 text-gray-400 border border-gray-600'
                }`}
              >
                <div className={`w-1.5 h-1.5 rounded-full ${skPerks.isSkHolder ? 'bg-green-400' : 'bg-gray-500'}`} />
                {skPerks.isSkHolder ? 'Verified Holder' : 'Not a Holder'}
              </div>
              {activeAddress && (
                <div className='mt-4'>
                  <Button onClick={handleVerify} loading={verifying} theme='outline'>
                    Re-verify
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div>
              <p className='text-gray-400 text-sm mb-4'>
                {activeAddress ? 'Verify your wallet to check holder status' : 'Connect a wallet first'}
              </p>
              {activeAddress && (
                <Button onClick={handleVerify} loading={verifying}>
                  Verify Holder Status
                </Button>
              )}
            </div>
          )}
        </DashboardCard>
      </div>

      <DashboardCard index={2} glowColor='from-yellow-500/20 via-transparent to-yellow-500/20'>
        <div className='flex items-center justify-between'>
          <div>
            <div className='flex items-center gap-2 mb-2'>
              <div className='w-2 h-2 rounded-full bg-yellow-500' />
              <h3 className='text-sm font-bold text-gray-400 uppercase tracking-wider'>Frik'd Up Fishies Coins</h3>
            </div>
            <p className='text-3xl font-bold text-yellow-400'>{coinBalance.toLocaleString()}</p>
            <p className='text-xs text-gray-500 mt-1'>Synced from Frik'd Up Fishies</p>
          </div>
          <div className='w-40 h-40 overflow-hidden opacity-60 select-none'>
            <img src='/fourth-litter/traits/accessories/Goldfish.png' alt='Goldfish' className='w-[500px] h-[500px] max-w-none -mt-[200px] -ml-[260px]' />
          </div>
        </div>
      </DashboardCard>
    </div>
  );
}
